<template>
  <div class="garage-collection-banner">
    <div
      v-if="vehicle"
      class="garage-collection-banner__active-vehicle"
    >
      <div class="garage-collection-banner__header">
        <p
          class="text-body-s-bold text-body-m-bold-desktop"
          v-text="$string('garage.banner.my_vehicle')"
        />

        <btn
          class="garage-collection-banner__change"
          class-label="text-body-xs-underline"
          :label="$string('garage.cta_labels.change')"
          modes="dark"
          modifiers="text"
          @click="openFinderFlow('garage')"
        />
      </div>

      <div class="garage-collection-banner__content">
        <garage-vehicle-details
          class="garage-collection-banner__vehicle"
          template="banner"
          :vehicle="vehicle"
        />

        <btn
          class="garage-collection-banner__view"
          :label="$string('garage.cta_labels.see_products')"
          modifiers="secondary center"
          :url="formattedVehicle?.resultsUrl"
        />

        <divider-line class="garage-collection-banner__divider" />

        <div class="garage-collection-banner__toggle-container">
          <p
            class="text-body-s-regular"
            v-text="$string('garage.banner.show_compatibility')"
          />

          <toggle-switch
            :id="`toggle-${namespace}`"
            v-model="localShowCompatibility"
            class="garage-collection-banner__toggle"
            :label-false="$string('garage.banner.hide_compatibility')"
            :label-true="$string('garage.banner.show_compatibility')"
          />
        </div>
      </div>
    </div>

    <div
      v-else
      class="garage-collection-banner__no-vehicle"
    >
      <garage-vehicle-details
        class="garage-collection-banner__vehicle"
        template="banner"
        :vehicle="vehicle"
      />

      <btn
        class="garage-collection-banner__add"
        :label="$string('garage.cta_labels.add')"
        modifiers="secondary center"
        type="button"
        @click="openFinderFlow('both')"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Garage collection banner (garage-collection-banner)
 * -----------------------------------------------------------------------------
 * Banner displayed at the top of collection pages that displays current
 * vehicle.
 *
 * @param {String} namespace - Unique namespace.
 * @param {Boolean|import("~stores/garage").Vehicle} vehicle - Vehicle.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import DividerLine from '~async/utils/divider-line/divider-line'
import GarageVehicleDetails from '~async/garage-vehicle-details/garage-vehicle-details'
import ToggleSwitch from '~async/utils/toggle-switch/toggle-switch'

import { validateVehicle } from '~/helpers/garage'

export default {
  name: 'GarageCollectionBanner',

  components: {
    Btn,
    DividerLine,
    GarageVehicleDetails,
    ToggleSwitch,
  },

  props: {
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    vehicle: {
      type: [Boolean, Object],
      default: false,
      required: true,
      validator: validateVehicle,
    },
  },

  data() {
    return {
      localShowCompatibility: true,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFormattedVehicle: 'garage/getFormattedVehicle',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      showCompatibility: (state) => state.garage.showCompatibility,
    }),

    /**
     * Compute formatted vehicle.
     * @returns {import("~stores/garage").FormattedVehicle}
     */
    formattedVehicle() {
      return this.getFormattedVehicle(this.vehicle)
    },
  },

  watch: {

    /**
     * Watch local show compatibility to update state.
     */
    localShowCompatibility() {
      this.setShowCompatibility(this.localShowCompatibility)
    },
  },

  mounted() {
    this.localShowCompatibility = this.showCompatibility
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openFinderFlow: 'garage/openFinderFlow',
      setShowCompatibility: 'garage/showCompatibility',
    }),
  },
}
</script>

<style lang="scss">
@import './garage-collection-banner';
</style>
